/**
 * Pure predicates for MainLayout's URL-driven module guard.
 *
 * The guard validates the URL's moduleId against the live conversation and
 * redirects stale URLs to a remembered/default module. Two situations must
 * HOLD the URL instead of redirecting:
 *
 *  - `pluginsStillLoading` (#2721): the URL points at a plugin instance and
 *    the plugin list hasn't resolved yet (with a 2s safety-timer release).
 *  - `paneIntentHoldsUrl` (ADR 028): the module-pane authority shows live
 *    intent for exactly the URL's module. A first-ever open of a workspace
 *    module in a conversation has no ConversationPanel yet, so the module is
 *    briefly absent from `activeConversation.modules` while the attach runs —
 *    redirecting in that window bounced the URL back to the previous module
 *    and the bounce's module.focus outranked the click's late attach-focus
 *    (the focus-sequence guard then discarded it): the "first click does
 *    nothing, second click works" bug. If the attach fails, the 1800ms
 *    missing-module cleanup closes the pane, the hold releases, and the
 *    redirect proceeds.
 */

export function pluginsStillLoadingHold(params: {
  moduleIdFromUrl: string | null;
  pluginInstancesLoaded: boolean;
  pluginGuardExpired: boolean;
}): boolean {
  const urlPointsAtPlugin = !!params.moduleIdFromUrl?.startsWith('plugin:');
  return urlPointsAtPlugin && !params.pluginInstancesLoaded && !params.pluginGuardExpired;
}

export function paneIntentHoldsUrl(params: {
  moduleIdFromUrl: string | null;
  moduleIdMatches: boolean;
  paneModuleId: string | null;
  isPaneVisible: boolean;
  /**
   * Whether the pane's current target came from a user/agent gesture
   * (ModulePaneContext provenance). A pure URL seed — e.g. a stale bookmark to
   * a deleted module — must NOT hold; it falls through to the immediate
   * redirect, exactly as before ADR 028.
   */
  paneTargetIsIntent: boolean;
}): boolean {
  const { moduleIdFromUrl, moduleIdMatches, paneModuleId, isPaneVisible, paneTargetIsIntent } = params;
  if (!moduleIdFromUrl || moduleIdMatches) return false;
  return paneTargetIsIntent && isPaneVisible && paneModuleId === moduleIdFromUrl;
}
